import { DetailedMeta } from "@/backend/metadata/getmeta";
import { MWMediaMeta, MWMediaType } from "@/backend/metadata/types/mw";

import { WatchedStoreData, WatchedStoreItem } from "../types";

interface OldMediaBase {
  mediaId: number;
  mediaType: MWMediaType;
  percentage: number;
  progress: number;
  providerId: string;
  title: string;
  year: number;
}

interface OldMovie extends OldMediaBase {
  mediaType: MWMediaType.MOVIE;
}

interface OldSeries extends OldMediaBase {
  mediaType: MWMediaType.SERIES;
  episodeId: number;
  seasonId: number;
}

export interface OldData {
  items: (OldMovie | OldSeries)[];
}

export interface OldBookmarks {
  bookmarks: (OldMovie | OldSeries)[];
}

// Search-based migration relied on TMDB and is no longer supported in the
// Jellyfin-only frontend.
async function getMetas(): Promise<
  Record<string, Record<string, DetailedMeta | null>> | undefined
> {
  return undefined;
}

export async function migrateV1Bookmarks(old: OldBookmarks) {
  const oldData = old;
  if (!oldData) return;

  const mediaMetas = await getMetas();
  if (!mediaMetas) return;

  const bookmarks = Object.keys(mediaMetas)
    .map((key) => mediaMetas[key]["0"])
    .map((t) => t?.meta)
    .filter(Boolean);

  return {
    bookmarks,
  };
}

export async function migrateV2Videos(old: OldData) {
  const oldData = old;
  if (!oldData) return;

  const mediaMetas = await getMetas();
  if (!mediaMetas) return;

  const newData: WatchedStoreData = {
    ...oldData,
    items: [],
  };

  const now = Date.now();

  for (const oldWatched of oldData.items) {
    if (oldWatched.mediaType === "movie") {
      if (!mediaMetas[oldWatched.mediaId]["0"]?.meta) continue;

      const newItem: WatchedStoreItem = {
        item: {
          meta: mediaMetas[oldWatched.mediaId]["0"]?.meta as MWMediaMeta,
        },
        progress: oldWatched.progress,
        percentage: oldWatched.percentage,
        watchedAt: Date.now(),
      };

      oldData.items = oldData.items.filter(
        (item) => JSON.stringify(item) !== JSON.stringify(oldWatched),
      );
      newData.items.push(newItem);
    } else if (oldWatched.mediaType === "series") {
      if (!mediaMetas[oldWatched.mediaId][oldWatched.seasonId]?.meta) continue;

      const meta = mediaMetas[oldWatched.mediaId][oldWatched.seasonId]
        ?.meta as MWMediaMeta;

      if (meta.type !== "series") return;

      const newItem: WatchedStoreItem = {
        item: {
          meta,
          series: {
            episode: Number(oldWatched.episodeId),
            season: Number(oldWatched.seasonId),
            seasonId: meta.seasonData.id,
            episodeId:
              meta.seasonData.episodes[Number(oldWatched.episodeId) - 1].id,
          },
        },
        progress: oldWatched.progress,
        percentage: oldWatched.percentage,
        watchedAt:
          now +
          Number(oldWatched.seasonId) * 1000 +
          Number(oldWatched.episodeId),
      };

      if (
        newData.items.find(
          (item) =>
            item.item.meta.id === newItem.item.meta.id &&
            item.item.series?.episodeId === newItem.item.series?.episodeId,
        )
      )
        continue;

      oldData.items = oldData.items.filter(
        (item) => JSON.stringify(item) !== JSON.stringify(oldWatched),
      );
      newData.items.push(newItem);
    }
  }

  return newData;
}
